import { PreferenceDto, SavedSearch } from '@creditsights/api-ui-web';
import React, { Dispatch, useEffect, useRef } from 'react';
import { RowSelection } from '../../components/Header/SearchBar/SearchSuggest/SearchSuggest.constants';
import { RecentSearch } from '../../models/RecentSearch';
import { SearchSuggestItem } from '../../models/SearchSuggest';

interface UseSearchSuggestKeyboardListenersProps {
  term: string;
  onSuggestionClick: (selectedTerm: string, category?: string) => void;
  navigateToSavedSearch: (savedSearch: SavedSearch) => void;
  setVerticalIndex: Dispatch<React.SetStateAction<number | null>>;
  verticalIndex: number | null;
  horizontalIndex: 0 | 1;
  setHorizontalIndex: Dispatch<React.SetStateAction<0 | 1>>;
  searchSuggest?: SearchSuggestItem[];
  savedSearches?: SavedSearch[];
  mostRecentSearches?: PreferenceDto<RecentSearch[]>;
  handleSearchSuggestClick: (suggest: SearchSuggestItem) => void;
  handleClearSearchClick: () => void;
}

const calculateNextVerticalIndex = (listCount: number, prevIndex: number | null): number =>
  prevIndex === null || prevIndex + 1 > listCount - 1 ? 0 : prevIndex + 1;

const calculatePrevVerticalIndex = (listCount: number, prevIndex: number | null): number =>
  prevIndex === null || prevIndex - 1 < 0 ? listCount - 1 : prevIndex - 1;

const getPrevRow = (listOfRows: NodeListOf<Element>): Element | null => {
  let prevElementIndex: number | undefined;
  listOfRows.forEach((element, index) => {
    if (element.getAttribute('data-row-selection') !== RowSelection.None) {
      prevElementIndex = index > 0 ? index - 1 : listOfRows.length - 1;
    }
  });
  return prevElementIndex !== undefined ? listOfRows.item(prevElementIndex) : null;
};

/**
 * Custom hook for handling events on dropdown items of search suggest.
 */
const useSearchSuggestKeyboardListeners = ({
  term,
  navigateToSavedSearch,
  setVerticalIndex,
  onSuggestionClick,
  searchSuggest,
  savedSearches,
  mostRecentSearches,
  verticalIndex,
  horizontalIndex,
  setHorizontalIndex,
  handleSearchSuggestClick,
  handleClearSearchClick,
}: UseSearchSuggestKeyboardListenersProps): void => {
  const actionButtonRef = useRef<HTMLButtonElement | null>(null);

  // navigation controllers
  const moveDown = (listLength) => setVerticalIndex((prevState) => calculateNextVerticalIndex(listLength, prevState));
  const moveUp = (listLength) => setVerticalIndex((prevState) => calculatePrevVerticalIndex(listLength, prevState));
  const moveRight = () => setHorizontalIndex(1);
  const moveLeft = () => setHorizontalIndex(0);

  const tabForward = (listLength) => {
    if (horizontalIndex === 0) {
      const currentRow = document?.querySelector(`[data-row-selection=${RowSelection.Item}]`);
      const currentRowButton = currentRow?.querySelector('button');

      if (currentRowButton) {
        moveRight();
        actionButtonRef.current = currentRowButton;
      } else {
        moveDown(listLength);
      }
    }
    if (horizontalIndex === 1) {
      moveLeft();
      moveDown(listLength);
      actionButtonRef.current = null;
    }
  };

  const tabBackward = (listOfRows) => {
    if (horizontalIndex === 0) {
      const prevRowButton = getPrevRow(listOfRows)?.querySelector('button');
      if (prevRowButton) {
        moveRight();
        actionButtonRef.current = prevRowButton;
      }
      moveUp(listOfRows.length);
    }
    if (horizontalIndex === 1) {
      moveLeft();
      actionButtonRef.current = null;
    }
  };

  const handleKeyDown = (event) => {
    const listOfRows = document.querySelectorAll('[data-row-selection]');
    if (!searchSuggest || !listOfRows.length) {
      return;
    }
    const { code } = event;
    const isEnterPressedOnActionButton = actionButtonRef.current && code === 'Enter';
    const isEnterPressedOnSearchSuggest = searchSuggest?.length && term && code === 'Enter' && verticalIndex !== null;
    const isEnterPressedOnSavedOrRecentSearchItem =
      !searchSuggest?.length && !term && code === 'Enter' && verticalIndex !== null;
    const mostRecentSearchesCount = mostRecentSearches?.value.length;

    if (isEnterPressedOnActionButton) {
      actionButtonRef.current?.click();
      if (actionButtonRef.current?.getAttribute('data-remove') && mostRecentSearchesCount === 1) {
        moveUp(listOfRows.length);
      }
      actionButtonRef.current = null;
      moveLeft();
      return;
    }

    if (isEnterPressedOnSearchSuggest) {
      handleSearchSuggestClick(searchSuggest[verticalIndex]);
      handleClearSearchClick();
      setVerticalIndex(null);
    }

    if (isEnterPressedOnSavedOrRecentSearchItem) {
      const savedSearchesCount = savedSearches?.length;
      const selectedItemOrderPlace = verticalIndex + 1;
      const isSavedSearchSelected = savedSearchesCount && selectedItemOrderPlace <= savedSearchesCount;
      const savedSearchesDisplayedAndSelectedRecentSearch =
        mostRecentSearchesCount && savedSearchesCount && selectedItemOrderPlace > savedSearchesCount;
      const savedSearchesNotDisplayedAndSelectedRecentSearch =
        mostRecentSearchesCount && !savedSearchesCount && selectedItemOrderPlace <= mostRecentSearchesCount;

      if (isSavedSearchSelected) {
        navigateToSavedSearch(savedSearches[verticalIndex]);
      }
      if (savedSearchesNotDisplayedAndSelectedRecentSearch) {
        onSuggestionClick(
          mostRecentSearches?.value[verticalIndex].searchTerm,
          mostRecentSearches?.value[verticalIndex].category
        );
      }
      if (savedSearchesDisplayedAndSelectedRecentSearch) {
        onSuggestionClick(
          mostRecentSearches?.value[verticalIndex - savedSearchesCount].searchTerm,
          mostRecentSearches?.value[verticalIndex - savedSearchesCount].category
        );
      }
    }

    const isShiftDown = event?.shiftKey;
    if (code === 'Tab' && !isShiftDown) {
      tabForward(listOfRows.length);
    }
    if (code === 'Tab' && isShiftDown) {
      tabBackward(listOfRows);
    }
    if (code === 'ArrowDown') {
      moveLeft();
      moveDown(listOfRows.length);
    }
    if (code === 'ArrowUp') {
      moveLeft();
      moveUp(listOfRows.length);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [verticalIndex, searchSuggest, mostRecentSearches, savedSearches, horizontalIndex, term]);
};

export default useSearchSuggestKeyboardListeners;
